// Field Tracking client.
//
// Mounted at /projects/:projectId/field-tracking - NOT /tracking, which
// belongs to the older EngineeringTracking feature.
import { url, tokenRef } from "api";

const getHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const userToken = localStorage.getItem(tokenRef);
  if (userToken) headers.Authorization = `Bearer ${userToken}`;
  return headers;
};

const handle = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    throw error;
  }
  return data;
};

const base = (projectId) => `${url}/projects/${projectId}/field-tracking`;

const qs = (params) => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === "all") return;
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

// The board: a PAGE of items with the filter applied on the server,
// the drawn items for the overlay, the sheets and the totals. The old
// answer was every item on the project - 21 MB on a 35,000-item job.
//   { page, limit, system, status, sheetId, drawn, from, to, q }
export const getBoard = (projectId, params) =>
  fetch(`${base(projectId)}${qs(params)}`, { headers: getHeaders() }).then(handle);

// EVERY ITEM AND SPAN ON THE BOARD as a file (owner, 2026-10-20): the board's
// own filters, no page - the server streams it. ext: "pdf" | "xlsx" | "csv".
export const exportBoard = async (projectId, ext, params) => {
  const res = await fetch(`${base(projectId)}/items/export.${ext}${qs(params)}`, { headers: getHeaders() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || "Export failed");
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = (((res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1]) || `tracking-items.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
  return { count: Number(res.headers.get("X-Export-Count") || 0) };
};

// The table's other pages, same filter vocabulary.
export const getItems = (projectId, params) =>
  fetch(`${base(projectId)}/items${qs(params)}`, { headers: getHeaders() }).then(handle);

export const getAnalytics = (projectId, { from, to, system } = {}) => {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  if (system && system !== "all") q.set("system", system);
  const qs = q.toString();
  return fetch(`${base(projectId)}/analytics${qs ? `?${qs}` : ""}`, { headers: getHeaders() }).then(handle);
};

// The hot path: the field marking a span done. Status and date travel
// together - the server refuses one without the other.
export const setSegmentStatus = (projectId, itemId, segmentKey, body) =>
  fetch(`${base(projectId)}/items/${itemId}/segments/${encodeURIComponent(segmentKey)}`, {
    method: "PATCH", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const bulkStatus = (projectId, body) =>
  fetch(`${base(projectId)}/items/bulk-status`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  }).then(handle);

export const createItem = (projectId, body) =>
  fetch(`${base(projectId)}/items`, { method: "POST", headers: getHeaders(), body: JSON.stringify(body) }).then(handle);

export const updateItem = (projectId, itemId, body) =>
  fetch(`${base(projectId)}/items/${itemId}`, { method: "PATCH", headers: getHeaders(), body: JSON.stringify(body) }).then(handle);

export const deleteItem = (projectId, itemId) =>
  fetch(`${base(projectId)}/items/${itemId}`, { method: "DELETE", headers: getHeaders() }).then(handle);

export const getSources = (projectId) =>
  fetch(`${base(projectId)}/sources`, { headers: getHeaders() }).then(handle);

export const addSource = (projectId, body) =>
  fetch(`${base(projectId)}/sources`, { method: "POST", headers: getHeaders(), body: JSON.stringify(body) }).then(handle);

export const deleteSource = (projectId, sourceId) =>
  fetch(`${base(projectId)}/sources/${sourceId}`, { method: "DELETE", headers: getHeaders() }).then(handle);

// A proposal arrives with its counts and a first page of rows; the
// rest of the rows come from getMatchRows.
export const getMatches = (projectId, status, params) =>
  fetch(`${base(projectId)}/matches${qs({ status, ...(params || {}) })}`, { headers: getHeaders() }).then(handle);

//   { page, limit, action } - action '!absent' is what the review table
//   shows, 'absent' the items the source did not mention.
// `category`: omit it for every row, pass a name for that category, or pass
// NO_CATEGORY for the rows that have none.
//
// NOT '' FOR THE LAST ONE. The qs() helper above drops empty strings, so a
// literal '' would be silently removed from the URL and the server would see
// no filter and return EVERY row - the "no category" group would quietly show
// the whole proposal. A sentinel survives the helper; the server maps it back.
export const NO_CATEGORY = '__none__';
export const getMatchRows = (projectId, matchId, params) =>
  fetch(`${base(projectId)}/matches/${matchId}/rows${qs(params)}`, { headers: getHeaders() }).then(handle);

export const decideMatch = (projectId, matchId, decisions) =>
  fetch(`${base(projectId)}/matches/${matchId}/decide`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify({ decisions }),
  }).then(handle);

// Apply every part of an import that is still waiting, one after another
// ON THE SERVER. The alternative is what people actually did - click
// Apply on each part - and eight of those at once took production down
// twice on 2026-10-02. A big import takes minutes, so whoever calls this
// must not offer a second click while it runs.
// IT RETURNS 202 AND THE WORK RUNS ON THE WORKER (owner, 2026-10-02).
// Six minutes is a 524 at the edge whatever the server does - Cloudflare
// gives an origin 100 seconds - so the request starts the job and the page
// follows it through getApplyStatus. The answer is { queued, runId,
// groupId, partsInGroup, lane }, NOT a totals object: a caller that prints
// `r.linked` now prints nothing, which is why its call site changed with
// this.
// A 409 means an apply is already running on the project, with the sentence.
export const applySourceGroup = (projectId, groupId, origin = "review") =>
  fetch(`${base(projectId)}/matches/group/${encodeURIComponent(groupId)}/apply`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify({ origin }),
  }).then(handle);

// What the spinners read: the newest apply run on the project, and whether
// it is genuinely live. `stale` means the job was killed - the page says so
// rather than spinning for ever, which is the failure this replaced.
export const getApplyStatus = (projectId) =>
  fetch(`${base(projectId)}/apply/status`, { headers: getHeaders() }).then(handle);
